import { cn } from "../lib/utils";
import type { CharacterResult } from "../types/domain";

export interface CharacterGridProps {
  characters: CharacterResult[];
  onSelect?: (recordId: string) => void;
  className?: string;
}

function scoreTone(score: number): "good" | "warn" | "accent" {
  if (score >= 75) return "good";
  if (score >= 60) return "warn";
  return "accent";
}

function scoreColorVar(tone: "good" | "warn" | "accent"): string {
  switch (tone) {
    case "good":
      return "var(--good)";
    case "warn":
      return "var(--warn)";
    case "accent":
      return "var(--accent)";
  }
}

export function CharacterGrid({ characters, onSelect, className }: CharacterGridProps) {
  if (characters.length === 0) {
    return (
      <div className={cn("text-sm text-[color:var(--text-muted)]", className)}>
        未识别出任何字符。
      </div>
    );
  }
  return (
    <div
      className={cn(
        "grid gap-3 grid-cols-[repeat(auto-fill,minmax(120px,1fr))]",
        className,
      )}
    >
      {characters.map((c) => {
        const tone = scoreTone(c.totalScore);
        const color = scoreColorVar(tone);
        const topIssue = c.issues?.[0] ?? "无问题";
        const clickable = Boolean(onSelect);
        return (
          <button
            key={c.recordId}
            type="button"
            disabled={!clickable}
            onClick={() => onSelect?.(c.recordId)}
            className={cn(
              "group flex flex-col items-stretch gap-2 rounded-[var(--radius-card)] border border-[color:var(--border)] bg-[color:var(--surface)] p-3 text-left transition-colors",
              clickable && "hover:bg-[color:var(--surface-2)] cursor-pointer",
              !clickable && "cursor-default",
            )}
          >
            <div className="flex items-center justify-between gap-2">
              <span className="font-serif text-3xl leading-none">{c.char}</span>
              <span
                className="rounded-full px-2 py-0.5 text-xs font-semibold tabular-nums"
                style={{
                  backgroundColor: `color-mix(in srgb, ${color} 12%, transparent)`,
                  color,
                }}
              >
                {Math.round(c.totalScore)}
              </span>
            </div>
            <p className="line-clamp-2 text-xs text-[color:var(--text-muted)]">
              {topIssue}
            </p>
          </button>
        );
      })}
    </div>
  );
}